/**
 * The REST client.
 *
 * Two behaviours the rest of the app relies on and never reimplements:
 *
 *  1. **One retry on an expired access token.** A 401 whose code says the token
 *     expired triggers a refresh and exactly one replay. Not a loop: if the
 *     replay 401s again the session is genuinely gone, and retrying would spin.
 *  2. **One refresh in flight at a time.** Three requests failing together share
 *     a single `POST /auth/refresh`. Without this they would race, and because
 *     refresh tokens rotate, the losers would present an already-spent token and
 *     trip the server's reuse detection — logging the user out for doing nothing
 *     wrong.
 */

import type { ApiErrorBody, PrivateUser } from '@ordago/shared';

import { runtimeConfig } from '../config';
import { useStore } from '../store';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** True when nothing reached the server — a different message to the user. */
  get isNetwork(): boolean {
    return this.status === 0;
  }
}

type Body = Record<string, unknown> | FormData | undefined;

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: Body;
  /** Set on the refresh call itself, so a failed refresh cannot refresh again. */
  skipRefresh?: boolean;
  signal?: AbortSignal;
}

function url(path: string): string {
  return `${runtimeConfig.apiBase}${path}`;
}

async function parseError(res: Response): Promise<ApiError> {
  let payload: ApiErrorBody | undefined;
  try {
    payload = (await res.json()) as ApiErrorBody;
  } catch {
    payload = undefined;
  }
  const error = payload?.error;
  return new ApiError(
    res.status,
    error?.code ?? 'INTERNAL',
    error?.message ?? res.statusText,
    error?.details,
  );
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const { method = 'GET', body, signal } = options;
  const token = useStore.getState().auth.accessToken;
  const isForm = body instanceof FormData;

  const headers: Record<string, string> = {};
  if (token !== null) headers['Authorization'] = `Bearer ${token}`;
  // Let the browser set the multipart boundary; an explicit content type here
  // produces a body the server cannot parse.
  if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json';

  const init: RequestInit = {
    method,
    headers,
    // Carries the refresh cookie. Same origin in every deployment, which is
    // the whole point of the single-hostname decision (frontend.md §7.1).
    credentials: 'include',
  };
  // Assigned rather than spread: under `exactOptionalPropertyTypes` an explicit
  // `undefined` body is not the same as no body.
  if (body !== undefined) init.body = isForm ? body : JSON.stringify(body);
  if (signal !== undefined) init.signal = signal;

  try {
    return await fetch(url(path), init);
  } catch (cause) {
    if (signal?.aborted === true) throw cause;
    throw new ApiError(0, 'NETWORK', cause instanceof Error ? cause.message : 'Network failure');
  }
}

// ────────────────────────────────────────────────────────── refresh, single-flight

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Exchanges the refresh cookie for a new access token. Resolves to whether a
 * session is now in the store; never throws.
 */
export function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      const res = await send('/auth/refresh', { method: 'POST', skipRefresh: true });
      if (!res.ok) return false;
      const data = (await res.json()) as { accessToken: string; user: PrivateUser };
      useStore.getState().auth.setSession(data);
      return true;
    } catch {
      return false;
    } finally {
      // Cleared in a microtask so callers that awaited this promise all see the
      // same result before a new refresh can start.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();
  return refreshInFlight;
}

function isExpiredToken(error: ApiError): boolean {
  return (
    error.status === 401 && (error.code === 'TOKEN_EXPIRED' || error.code === 'UNAUTHENTICATED')
  );
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let res = await send(path, options);

  if (res.status === 401 && options.skipRefresh !== true) {
    const error = await parseError(res);
    if (isExpiredToken(error)) {
      const refreshed = await refreshSession();
      if (!refreshed) {
        useStore.getState().auth.clear();
        throw error;
      }
      res = await send(path, options);
    } else {
      // A wrong password is a 401 too, and refreshing would not help.
      throw error;
    }
  }

  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, signal === undefined ? {} : { signal }),
  post: <T>(path: string, body?: Body) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: Body) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: Body) => request<T>(path, { method: 'PUT', body }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

// ──────────────────────────────────────────────────────────────── auth endpoints

export interface SessionResponse {
  accessToken: string;
  user: PrivateUser;
}

export const authApi = {
  register: (body: { username: string; email: string; password: string; locale?: string }) =>
    api.post<{ userId: string | null }>('/auth/register', body),

  verify: (body: { email: string; code: string }) =>
    api.post<SessionResponse>('/auth/verify', body),

  resend: (body: { email: string }) => api.post<Record<string, never>>('/auth/verify/resend', body),

  login: (body: { identifier: string; password: string }) =>
    api.post<SessionResponse>('/auth/login', body),

  logout: () => api.post<void>('/auth/logout'),

  logoutAll: () => api.post<void>('/auth/logout-all'),

  forgotPassword: (body: { email: string }) =>
    api.post<Record<string, never>>('/auth/password/forgot', body),

  resetPassword: (body: { token: string; password: string }) =>
    api.post<void>('/auth/password/reset', body),

  me: () => api.get<{ user: PrivateUser }>('/auth/me'),
};
